/**
 * PowerLogCard Component
 * Mobile card view for a single power log entry
 */

import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatCurrencyWithSymbol, formatDate } from '../utils/formatters';

const PowerLogCard = ({ log, onEdit, onDelete }) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">
            Power: {log.powerNo}
          </span>
          {log.amountOfBond != null && (
            <span className="ml-3 text-sm font-medium text-teal-600 dark:text-teal-400">
              {formatCurrencyWithSymbol(log.amountOfBond)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="p-2 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/30 rounded-md transition-colors"
            aria-label="Edit entry"
          >
            <PencilIcon className="h-5 w-5" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-md transition-colors"
            aria-label="Delete entry"
          >
            <TrashIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-2">
        {/* Defendant */}
        {log.name && (
          <div className="flex items-start">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
              Defendant:
            </span>
            <span className="text-sm text-gray-900 dark:text-white">
              {log.name}
            </span>
          </div>
        )}

        {/* Case Number */}
        {log.caseNumber && (
          <div className="flex items-start">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
              Case #:
            </span>
            <span className="text-sm text-gray-900 dark:text-white">
              {log.caseNumber}
            </span>
          </div>
        )}

        {/* Date Executed */}
        {log.dateExecuted && (
          <div className="flex items-start">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
              Executed:
            </span>
            <span className="text-sm text-gray-900 dark:text-white">
              {formatDate(log.dateExecuted)}
            </span>
          </div>
        )}

        {/* Premium */}
        {log.premiumCharged != null && (
          <div className="flex items-start">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
              Premium:
            </span>
            <span className="text-sm text-gray-900 dark:text-white">
              {formatCurrencyWithSymbol(log.premiumCharged)}
            </span>
          </div>
        )}

        {/* Disposition */}
        {(log.disposition || log.dispositionDate) && (
          <div className="flex items-start">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
              Disposition:
            </span>
            <span className="text-sm text-gray-900 dark:text-white">
              {log.disposition || '-'}
              {log.dispositionDate && (
                <span className="text-gray-500 dark:text-gray-400 ml-1">
                  ({formatDate(log.dispositionDate)})
                </span>
              )}
            </span>
          </div>
        )}

        {/* Agent */}
        {log.agentsName && (
          <div className="flex items-start">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
              Agent:
            </span>
            <span className="text-sm text-gray-900 dark:text-white">
              {log.agentsName}
            </span>
          </div>
        )}

        {/* Collateral */}
        {(log.collateral || log.collateralReceiptNo) && (
          <div className="flex items-start pt-2 border-t border-gray-100 dark:border-gray-700 mt-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-24 flex-shrink-0">
              Collateral:
            </span>
            <div className="text-sm text-gray-900 dark:text-white">
              {log.collateralReceiptNo && (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Receipt #: {log.collateralReceiptNo}
                </div>
              )}
              {log.collateral && (
                <div className="text-gray-600 dark:text-gray-300 text-xs mt-1">
                  {log.collateral}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PowerLogCard;
